import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LogIn, ChevronDown, ChevronUp, Star, Zap } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PredefinedActionCard } from './PredefinedActionCard';
import { PredefinedToolAuthModal } from './PredefinedToolAuthModal';
import { prebuiltActionsService } from '@/services/prebuiltActionsService';
import { useToast } from '@/hooks/use-toast';

interface PredefinedAction {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
}
interface PredefinedTool {
  id: string;
  name: string;
  description: string;
  category: string;
  logoUrl: string;
  isPopular: boolean;
  tags: string[];
  totalActions: number;
  activeActions: number;
  actions: PredefinedAction[];
  connected?: boolean;
}
interface ToolCardProps {
  tool: PredefinedTool;
  variant?: 'grid' | 'list';
}
export const ToolCard = ({
  tool,
  variant = 'grid'
}: ToolCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const { toast } = useToast();

  const handleSignIn = async () => {
    setIsSigningIn(true);
    try {
      const response: any = await prebuiltActionsService.generatePrebuildAppConnections(tool.name);
      if (response.data.redirectUrl) {
        // Redirect to the returned URL
        window.location.href = response.data.redirectUrl;
      } else {
        throw new Error('No redirect URL received');
      }
    } catch (error) {
      toast({
        title: 'Sign In Failed',
        description: 'Unable to sign in to the tool. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleAuthConfig = () => {
    console.log('Opening auth config for tool:', tool.name);
    setIsAuthModalOpen(true);
  };

  const handleEditAction = (actionId: string) => {
    console.log('Editing action:', actionId);
  };

  const handleToggleAction = (actionId: string, isActive: boolean) => {
    console.log('Toggling action:', actionId, 'to', isActive);
  };

  // Enhanced actions with mock API configuration
  const enhancedActions = tool?.actions?.map(action => ({
    ...action,
    endpoint: `https://api.${tool.name.toLowerCase().replace(' ', '')}.com/v1/${action.name.toLowerCase().replace(' ', '-')}`,
    method: 'POST',
    parameters: [
      {
        name: 'message',
        type: 'string',
        required: true,
        description: 'The message content to process'
      },
      {
        name: 'channel',
        type: 'string',
        required: false,
        description: 'Target channel or recipient'
      },
      {
        name: 'priority',
        type: 'string',
        required: false,
        description: 'Message priority level',
        default: 'normal'
      }
    ],
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer {{API_KEY}}',
      'X-API-Version': '1.0'
    }
  }));

  return <>
    <div className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md hover:border-slate-300 transition-all duration-200">
      {/* Compact Tool Header */}
      <div className="p-3 space-y-3">
        <div className="flex items-start gap-3">
          {/* Tool Logo */}
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm">
            <img src={tool.logoUrl} alt={`${tool.name} logo`} className="w-6 h-6 object-contain" onError={e => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }} />
          </div>
          
          {/* Tool Content */}
          <div className="flex-1 min-w-0">
            {/* Header row with title and configure button */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-slate-900 text-sm truncate">
                    {tool.name}
                  </h3>
                  {tool.isPopular}
                </div>
                <Badge variant="outline" className="text-xs px-1.5 py-0.5 bg-slate-50 text-slate-600 border-slate-200">
                  {tool.category}
                </Badge>
              </div>
              
              {/* Conditional Button - Sign In or Connected */}
              {tool.connected ? (
                <Badge variant="default" className="flex-shrink-0 h-7 px-2 text-xs font-medium bg-green-100 text-green-700 border-green-200">
                  <div className="h-1.5 w-1.5 bg-green-500 rounded-full mr-1.5"></div>
                  Connected
                </Badge>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleSignIn} 
                  disabled={isSigningIn}
                  className="flex-shrink-0 h-7 px-2 text-xs font-medium hover:bg-slate-50 hover:border-slate-300"
                >
                  <LogIn className="h-3 w-3 mr-1" />
                  {isSigningIn ? 'Signing In...' : 'Sign In'}
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* Description */}
        <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
          {tool.description}
        </p>

        {/* Stats and Tags Row */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
              {tool.totalActions} Total
            </span>
            <span className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              {tool.activeActions} Active
            </span>
          </div>

          {/* Compact Tags */}
          <div className="flex flex-wrap gap-1">
            {tool?.tags?.slice(0, 2).map(tag => <Badge key={tag} variant="secondary" className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
                {tag}
              </Badge>)}
            {tool?.tags?.length > 2 && <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-500">
                +{tool?.tags?.length - 2}
              </Badge>}
          </div>
        </div>
      </div>

      {/* Actions Section */}
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <div className="px-3 py-2 bg-gradient-to-r from-slate-50 to-slate-50/50 border-t border-slate-100 cursor-pointer hover:bg-gradient-to-r hover:from-slate-100 hover:to-slate-50 transition-all duration-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                  <Zap className="h-3 w-3 text-violet-600" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900">
                    Actions
                  </span>
                  <span className="text-xs text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded-full">
                    {tool?.actions?.length}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <span>{isExpanded ? 'Hide' : 'Show'}</span>
                {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </div>
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="p-3 space-y-2 bg-slate-50/30 border-t border-slate-100 max-h-64 overflow-y-auto">
            {enhancedActions?.map(action => <PredefinedActionCard key={action.id} action={action} onEdit={handleEditAction} onToggleActive={handleToggleAction} />)}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>

    <PredefinedToolAuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} toolName={tool.name} toolId={tool.id} />
  </>;
};
